import {isFeatureEnabled} from 'feature-flags';
import {OptionType} from 'platform/components';

import {head, isNil, isNotNil} from 'ramda';
import {isValidNumber} from 'ramda-adjunct';

import {getSafely, isNotNilOrEmpty, Nullish} from 'shared/web';

import {isTransmissionExcluded} from '../constants/transmissionOptionsExcluded';
import {FilterFormAddress, FilterFormData} from '../types/FilterFormData';
import {PriceReportType} from '../types/PriceReportType';
import {featureFlags} from './featureFlags';
import {getSimilarBodyStyles} from './getSimilarBodyStyles';
import {getYearOptions} from './getYearOptions';
import {mapMarketToAdServer} from './mapMarketToAdServer';

const getDefaultValue = (
  value: number,
  options: OptionType<number, string>[],
  couldBeSame = false
) => {
  if (couldBeSame) {
    const foundValue = options.find((option) => parseInt(String(option.value)) === value)?.value;
    if (isValidNumber(foundValue)) {
      return {from: foundValue, to: foundValue};
    }
  }

  const toIndex = options.findIndex((option) => parseInt(String(option.value)) > value);

  const fromValue = getSafely(options, toIndex - 1)?.value;
  const toValue = getSafely(options, toIndex)?.value;

  return {from: fromValue, to: toValue};
};

const getDefaultServer = (priceReport: PriceReportType | Nullish) => {
  if (isNil(priceReport?.market) || !isFeatureEnabled(featureFlags.PR_MULTI_SERVERS)) {
    return undefined;
  }

  const adServers = mapMarketToAdServer(priceReport.market);
  const isMultiServers = adServers.length > 1;

  return isMultiServers ? [head(adServers)!] : undefined;
};

const getDefaultAddress = (priceReport: PriceReportType | Nullish): FilterFormAddress | null => {
  const location = priceReport?.vehicle?.location;
  const label = location?.address;
  const lat = location?.lat;
  const lon = location?.lon;

  if (isNotNilOrEmpty(label) && isNotNil(lat) && isNotNil(lon)) {
    return {label, lat, lng: lon};
  }

  if (isNotNilOrEmpty(label)) {
    return {label};
  }

  return null;
};

export const getDefaultValues = (priceReport: PriceReportType | Nullish): FilterFormData => ({
  address: getDefaultAddress(priceReport),
  radius: null,
  title: null,
  showSoldIn30: true,
  showSoldIn90: true,
  showStockCars: true,
  priceGte: null,
  priceLte: null,
  isVatDeductible: [],
  firstRegistrationOnYearGte: priceReport?.vehicle
    ? getDefaultValue(priceReport.vehicle.firstRegistrationOnYear, getYearOptions(), true)?.from
    : null,
  firstRegistrationOnYearLte: priceReport?.vehicle
    ? getDefaultValue(priceReport.vehicle.firstRegistrationOnYear, getYearOptions(), true)?.to
    : null,
  mileageGte: null,
  mileageLte: null,
  daysInAdvertisementGte: null,
  daysInAdvertisementLte: null,
  transmission:
    priceReport?.vehicle && !isTransmissionExcluded(priceReport.vehicle.transmission)
      ? [priceReport.vehicle.transmission]
      : [],
  fuelType: priceReport?.vehicle ? [priceReport.vehicle.fuelType] : [],
  powerGte: priceReport?.vehicle?.maxPower ?? priceReport?.vehicle?.power,
  powerLte: priceReport?.vehicle?.maxPower ?? priceReport?.vehicle?.power,
  bodyStyle: getSimilarBodyStyles(priceReport?.vehicle?.bodyStyle),
  drive:
    priceReport?.vehicle &&
    priceReport.vehicle.drive !== 'DRIVE_REAR' &&
    priceReport.vehicle.drive !== 'DRIVE_FRONT'
      ? [priceReport.vehicle.drive]
      : ['DRIVE_4X2'],
  trimLevels: [],
  features: [],
  featureScoreGte: null,
  featureScoreLte: null,
  sellerType: [],
  server: getDefaultServer(priceReport),
});
